/**
 * App.js with Client-Side Routing & Page Transitions
 * * Description:
 * This version adds a simple fade-in animation for page transitions,
 * creating a smoother user experience.
 * * How to Use:
 * Replace the contents of your 'src/App.js' file with this code, and
 * add the corresponding animation CSS to 'src/App.css'.
 */
import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import './App.css';

// Import the components for your pages
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/Home';
import ManagerPage from './pages/manager/Manager';
import DealsPage from './pages/DealsPage';
import RetailerPage from './pages/RetailerPage';
import AboutPage from './pages/AboutPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsPage from './pages/TermsPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import CookiePolicy from './pages/CookiePolicy';
import CompensationDisclosure from './pages/CompensationDisclosure';
import TutorialVendorPage from './pages/Tutorial/VendorPage';
import TutorialStorePage from './pages/Tutorial/StorePage';
import ExtensionPage from './pages/ExtensionPage';


import { ThemeProvider } from './contexts/ThemeContext';

export default function App() {
    // State now holds the current URL path, initialized from the browser
    const [path, setPath] = useState(window.location.pathname);

    // Lifted auth state
    const [user, setUser] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);

    // This function updates the URL and triggers a re-render
    const navigate = (newPath) => {
        window.history.pushState({}, '', newPath);
        setPath(newPath);
    };

    // This effect handles the browser's back/forward buttons
    useEffect(() => {
        const onPopState = () => setPath(window.location.pathname);
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    // Auth listener & Sync
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setIsAuthReady(true);

            // Sync auth state with extension
            if (currentUser) {
                window.postMessage({
                    type: 'GIFT_SCOUT_AUTH_SYNC',
                    payload: {
                        uid: currentUser.uid,
                        email: currentUser.email,
                        displayName: currentUser.displayName
                    }
                }, '*');
            }
        });
        return () => unsubscribe();
    }, []);



    // The renderPage function now uses the URL path for routing
    const renderPage = () => {
        let pageComponent;

        if (path.startsWith('/retailer/')) {
            const slug = path.split('/')[2];
            pageComponent = <RetailerPage slug={slug} navigate={navigate} />;
        } else if (path === '/tutorial/vendor') {
            pageComponent = <TutorialVendorPage />;
        } else if (path === '/tutorial/store') {
            pageComponent = <TutorialStorePage />;
        } else {
            switch (path.split('?')[0]) { // Handle query params in path matching
                case '/':
                case '/home':
                    pageComponent = <HomePage navigate={navigate} />;
                    break;
                case '/tool':
                    pageComponent = <ManagerPage />;
                    break;
                case '/login':
                    pageComponent = <LoginPage navigate={navigate} isForExtension={false} />;
                    break;
                case '/login/extension':
                    pageComponent = <LoginPage navigate={navigate} isForExtension={true} />;
                    break;
                case '/deals':
                    pageComponent = <DealsPage navigate={navigate} />;
                    break;
                case '/about':
                    pageComponent = <AboutPage />;
                    break;
                case '/privacy':
                    pageComponent = <PrivacyPolicy />;
                    break;
                case '/terms':
                    pageComponent = <TermsPage />;
                    break;
                case '/contact':
                    pageComponent = <ContactPage />;
                    break;
                case '/disclosure':
                    pageComponent = <CompensationDisclosure />;
                    break;
                case '/cookies':
                    pageComponent = <CookiePolicy />;
                    break;
                case '/extension':
                    pageComponent = <ExtensionPage navigate={navigate} />;
                    break;

                default:
                    // A simple 404 page for any other URL
                    pageComponent = (
                        <div className="text-center p-12">
                            <h1 className="text-4xl font-bold dark:text-gray-100">404 - Page Not Found</h1>
                        </div>
                    );
            }
        }

        // This wrapper enables the page transition animation.
        // The `key` prop ensures the animation re-runs on every path change.
        return <div key={path} className="page-container">{pageComponent}</div>;
    };

    return (
        <ThemeProvider>
            <div className="bg-slate-50 dark:bg-slate-900 min-h-screen text-gray-800 dark:text-gray-100 flex flex-col transition-colors duration-200">
                <Navbar navigate={navigate} currentPath={path} user={user} isAuthReady={isAuthReady} />

                <main className="flex-grow">
                    {renderPage()}
                </main>

                <Footer navigate={navigate} />
            </div>
        </ThemeProvider>
    );
}